import {
  AnalyticsInputMessagePayload,
  AnalyticsInputRecord,
  AnalyticsInputTypesRegistry,
  AnonymousAppUserEvent,
  UserAnalyticsInfo,
} from '@ilteducation/analytics-gateway-types';
import { v4 as uuidv4 } from 'uuid';
import { useConfigHelper } from '@ilteducation/config';
import axios, { AxiosRequestConfig } from 'axios';

const { getApiUrl } = useConfigHelper();

const buildRequestBody = <T extends keyof AnalyticsInputTypesRegistry>(
  eventType: T,
  data: AnalyticsInputTypesRegistry[T],
): AnalyticsInputMessagePayload => {
  const record: AnalyticsInputRecord = {
    eventType,
    metadata: {
      eventUID: uuidv4(),
      timestamp: new Date().toISOString(),
    },
    data,
  };

  return { records: [record] };
};

export const postAnalyticsEvent = async <T extends keyof AnalyticsInputTypesRegistry>(
  eventType: T,
  data: AnalyticsInputTypesRegistry[T],
  isSentWhenPageIsHidden: boolean = false,
) => {
  const requestConfig: AxiosRequestConfig = isSentWhenPageIsHidden
    ? {
        adapter: 'fetch',
        fetchOptions: {
          keepalive: true,
        },
      }
    : {};
  await axios.post(
    `${getApiUrl('analytics-gateway', true)}/load-analytics-events`,
    buildRequestBody(eventType, data),
    requestConfig,
  );
};

export type ShareTarget = {
  shareId: string;
  openId: string;
  // The book the share is for; listening to any other book in the tab is not about this share.
  contentId: string;
  // A guest's player events go through the share; a signed-in reader's keep their normal routes.
  asGuest: boolean;
};

let shareTarget: ShareTarget | null = null;

/**
 * Remembers the share this tab was opened from. For a guest it also points player analytics at the
 * share; see the activity-stream package for the twin of this: both transports keep their own body
 * shape and only change where they post.
 *
 * Call it once, after the open resolves, before the player mounts. Pass null to go back to the
 * normal routes.
 */
export const setShareTarget = (target: ShareTarget | null) => {
  shareTarget = target;
};

export const getShareTarget = (): ShareTarget | null => shareTarget;

/**
 * Posts over shared-content's public forwarding route, which stamps the user envelope from the
 * share row and passes the record on to the gateway.
 *
 * Unlike the anonymous route below, the event keeps its real type: shared-content is trusted, so a
 * player event stays a player event instead of being folded into a generic app-user row.
 *
 * The user envelope is not part of the payload and cannot be: an anonymous listener has none, and the
 * route stamps it from the share row. The type says so, so nobody can add one back by accident.
 */
export const postShareAnalyticsEvent = async <T extends keyof AnalyticsInputTypesRegistry>(
  eventType: T,
  data: Omit<AnalyticsInputTypesRegistry[T], keyof UserAnalyticsInfo>,
  isSentWhenPageIsHidden: boolean = false,
) => {
  if (!shareTarget) {
    return;
  }

  const requestConfig: AxiosRequestConfig = isSentWhenPageIsHidden
    ? { adapter: 'fetch', fetchOptions: { keepalive: true } }
    : {};

  await axios.post(
    `${getApiUrl('shared-contents')}/public/${shareTarget.shareId}/analytics-events`,
    // The route's schema takes the payload without the user envelope; buildRequestBody is typed for
    // the complete event, which is the one thing it cannot be here.
    buildRequestBody(eventType, data as AnalyticsInputTypesRegistry[T]),
    { ...requestConfig, noAuth: true },
  );
};

/**
 * Posts over the public, unauthenticated route. It accepts AnonymousAppUserEventType only, and
 * rejects the whole batch on anything else.
 *
 * noAuth is required, not decorative: ilt-auth's request interceptor cancels any request it cannot
 * attach a token to, which is every request an anonymous visitor makes.
 */
export const postAnonymousAnalyticsEvent = async (
  data: AnonymousAppUserEvent,
  isSentWhenPageIsHidden: boolean = false,
) => {
  const requestConfig: AxiosRequestConfig = isSentWhenPageIsHidden
    ? { adapter: 'fetch', fetchOptions: { keepalive: true } }
    : {};
  await axios.post(
    `${getApiUrl('analytics-gateway', true)}/load-anonymous-analytics-events`,
    buildRequestBody('AnonymousAppUserEventType', data),
    { ...requestConfig, noAuth: true },
  );
};
